/**
 * One Euro Filter 기반 랜드마크 스무딩
 *
 * 기존의 "이동거리 비례 alpha" 방식은 완전 정지 상태에서도 alpha 하한(0.2~0.35)이
 * 남아 있어, MediaPipe 원본 좌표의 프레임 간 서브픽셀 노이즈가 그대로 새어 나와
 * 정지 시에도 스켈레톤이 미세하게 흔들려 보이는 문제가 있었다.
 *
 * One Euro Filter는 속도(직전 대비 변화율)에 따라 컷오프 주파수를 적응적으로
 * 낮추므로, 정지 시에는 노이즈를 강하게 억제하면서도 빠른 동작에는 지연 없이
 * 반응한다. (참고: Casiez et al., "1€ Filter", CHI 2012 — AR/포즈 트래킹 표준 기법)
 */
class LowPassFilter {
  constructor() {
    this.hasValue = false;
    this.value = 0;
  }

  filter(x, alpha) {
    this.value = this.hasValue ? alpha * x + (1 - alpha) * this.value : x;
    this.hasValue = true;
    return this.value;
  }
}

class OneEuroFilter {
  /**
   * @param {number} minCutoff - 낮을수록 정지 시 더 강하게 스무딩(값이 더 안정적)
   * @param {number} beta - 높을수록 빠른 움직임에 더 빨리 반응(지연 감소)
   * @param {number} dCutoff - 속도 추정 자체의 스무딩 강도
   */
  constructor(minCutoff = 0.4, beta = 0.8, dCutoff = 1.0) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dCutoff = dCutoff;
    this.xFilter = new LowPassFilter();
    this.dxFilter = new LowPassFilter();
    this.lastTimeSec = null;
  }

  static alpha(cutoff, dt) {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  filter(value, timeSec) {
    // 타임스탬프가 없거나 비정상(역행/정지)이면 안전한 기본 dt로 대체
    let dt = 1 / 30;
    if (this.lastTimeSec != null && timeSec != null && timeSec > this.lastTimeSec) {
      dt = timeSec - this.lastTimeSec;
    }
    this.lastTimeSec = timeSec ?? this.lastTimeSec;

    const prevValue = this.xFilter.hasValue ? this.xFilter.value : value;
    const dx = (value - prevValue) / dt;
    const edx = this.dxFilter.filter(dx, OneEuroFilter.alpha(this.dCutoff, dt));

    const cutoff = this.minCutoff + this.beta * Math.abs(edx);
    return this.xFilter.filter(value, OneEuroFilter.alpha(cutoff, dt));
  }

  reset() {
    this.xFilter.hasValue = false;
    this.dxFilter.hasValue = false;
    this.lastTimeSec = null;
  }
}

export class PoseSmoother {
  /**
   * @param {number} minCutoff - 정지 시 안정도 (작을수록 안정적, 0.2~0.6 권장)
   * @param {number} beta - 움직임 반응성 (클수록 빠른 동작 추적, 0.4~1.2 권장)
   */
  constructor(minCutoff = 0.4, beta = 0.8) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.filters = null; // landmark index -> { x, y, z }
  }

  smooth(landmarks, timestampMs) {
    if (!landmarks || landmarks.length === 0) {
      this.filters = null;
      return landmarks;
    }

    if (!this.filters || this.filters.length !== landmarks.length) {
      this.filters = landmarks.map(
        () => ({
          x: new OneEuroFilter(this.minCutoff, this.beta),
          y: new OneEuroFilter(this.minCutoff, this.beta),
          z: new OneEuroFilter(this.minCutoff, this.beta),
        })
      );
      return landmarks.map((pt) => ({ ...pt }));
    }

    const timeSec = typeof timestampMs === 'number' ? timestampMs / 1000 : null;

    return landmarks.map((curr, idx) => {
      const f = this.filters[idx];
      return {
        ...curr,
        x: f.x.filter(curr.x, timeSec),
        y: f.y.filter(curr.y, timeSec),
        z: curr.z !== undefined ? f.z.filter(curr.z, timeSec) : curr.z,
        visibility: curr.visibility,
      };
    });
  }

  reset() {
    this.filters = null;
  }
}

export const poseSmoother = new PoseSmoother(0.4, 0.8);
